import {
  DiffResult,
  DiffHunk,
  DiffLine,
  SplitDiffLine,
  UnifiedDiffLine,
  WordToken,
  DiffStats,
} from '../types';

/**
 * Splits text into lines.
 */
function getLines(text: string): string[] {
  if (!text) return [];
  return text.split(/\r?\n/);
}

/**
 * Tokenizes text into word and whitespace chunks.
 */
function tokenizeWords(text: string): string[] {
  const tokens: string[] = [];
  const regex = /(\s+|[^\s\w]+|\w+)/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push(match[0]);
  }
  return tokens;
}

/**
 * Computes word-level diff tokens between two string lines.
 */
export function computeWordTokens(oldLine: string, newLine: string): { oldTokens: WordToken[]; newTokens: WordToken[] } {
  const oldWords = tokenizeWords(oldLine);
  const newWords = tokenizeWords(newLine);

  const oldTokens: WordToken[] = [];
  const newTokens: WordToken[] = [];

  let i = 0;
  let j = 0;

  while (i < oldWords.length && j < newWords.length) {
    if (oldWords[i] === newWords[j]) {
      oldTokens.push({ type: 'unchanged', text: oldWords[i] });
      newTokens.push({ type: 'unchanged', text: newWords[j] });
      i++;
      j++;
    } else {
      // Lookahead matching
      const nextInNew = newWords.indexOf(oldWords[i], j);
      const nextInOld = oldWords.indexOf(newWords[j], i);

      if (nextInNew !== -1 && (nextInOld === -1 || nextInNew - j <= nextInOld - i)) {
        while (j < nextInNew) {
          newTokens.push({ type: 'add', text: newWords[j] });
          j++;
        }
      } else if (nextInOld !== -1) {
        while (i < nextInOld) {
          oldTokens.push({ type: 'del', text: oldWords[i] });
          i++;
        }
      } else {
        oldTokens.push({ type: 'del', text: oldWords[i] });
        newTokens.push({ type: 'add', text: newWords[j] });
        i++;
        j++;
      }
    }
  }

  while (i < oldWords.length) {
    oldTokens.push({ type: 'del', text: oldWords[i] });
    i++;
  }
  while (j < newWords.length) {
    newTokens.push({ type: 'add', text: newWords[j] });
    j++;
  }

  return { oldTokens, newTokens };
}

/**
 * Calculates a structured line-by-line diff between two text documents.
 */
export function calculateStructuredDiff(
  oldText: string,
  newText: string,
  baseRevId: string = 'base',
  targetRevId: string = 'target',
  ignoreWhitespace: boolean = false
): DiffResult {
  const oldLines = getLines(oldText);
  const newLines = getLines(newText);

  let oldIdx = 0;
  let newIdx = 0;

  const splitLines: SplitDiffLine[] = [];
  const unifiedLines: UnifiedDiffLine[] = [];
  const hunks: DiffHunk[] = [];

  let linesAdded = 0;
  let linesRemoved = 0;
  let linesModified = 0;

  let currentHunkLines: DiffLine[] = [];
  let currentHunkType: 'addition' | 'deletion' | 'modification' | 'unchanged' = 'unchanged';

  const flushHunk = (heading?: string) => {
    if (currentHunkLines.length > 0) {
      hunks.push({
        hunkIndex: hunks.length,
        changeType: currentHunkType,
        sectionHeading: heading,
        lines: [...currentHunkLines],
      });
      currentHunkLines = [];
    }
  };

  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    const oLine = oldLines[oldIdx];
    const nLine = newLines[newIdx];

    const cleanO = ignoreWhitespace && oLine ? oLine.trim() : oLine;
    const cleanN = ignoreWhitespace && nLine ? nLine.trim() : nLine;

    // Both lines present and equal
    if (oldIdx < oldLines.length && newIdx < newLines.length && cleanO === cleanN) {
      if (currentHunkType !== 'unchanged') {
        flushHunk();
        currentHunkType = 'unchanged';
      }

      const diffLine: DiffLine = {
        old_lineno: oldIdx + 1,
        new_lineno: newIdx + 1,
        type: ' ',
        content: nLine,
      };

      currentHunkLines.push(diffLine);
      unifiedLines.push(diffLine);
      splitLines.push({
        leftLineNumber: oldIdx + 1,
        leftContent: oLine,
        leftType: 'unchanged',
        rightLineNumber: newIdx + 1,
        rightContent: nLine,
        rightType: 'unchanged',
      });

      oldIdx++;
      newIdx++;
    } else if (oldIdx < oldLines.length && newIdx < newLines.length) {
      // Potential line modification
      if (currentHunkType === 'unchanged') {
        flushHunk();
        currentHunkType = 'modification';
      }

      const { oldTokens, newTokens } = computeWordTokens(oLine, nLine);

      const delLine: DiffLine = {
        old_lineno: oldIdx + 1,
        new_lineno: null,
        type: '-',
        content: oLine,
        word_tokens: oldTokens,
      };
      const addLine: DiffLine = {
        old_lineno: null,
        new_lineno: newIdx + 1,
        type: '+',
        content: nLine,
        word_tokens: newTokens,
      };

      currentHunkLines.push(delLine, addLine);
      unifiedLines.push(delLine, addLine);
      splitLines.push({
        leftLineNumber: oldIdx + 1,
        leftContent: oLine,
        leftType: 'deleted',
        leftWordTokens: oldTokens,
        rightLineNumber: newIdx + 1,
        rightContent: nLine,
        rightType: 'added',
        rightWordTokens: newTokens,
      });

      linesModified++;
      linesRemoved++;
      linesAdded++;
      oldIdx++;
      newIdx++;
    } else if (oldIdx < oldLines.length) {
      // Deletion only
      if (currentHunkType === 'unchanged') {
        flushHunk();
        currentHunkType = 'deletion';
      }

      const delLine: DiffLine = {
        old_lineno: oldIdx + 1,
        new_lineno: null,
        type: '-',
        content: oLine,
      };

      currentHunkLines.push(delLine);
      unifiedLines.push(delLine);
      splitLines.push({
        leftLineNumber: oldIdx + 1,
        leftContent: oLine,
        leftType: 'deleted',
        rightLineNumber: undefined,
        rightContent: undefined,
        rightType: 'empty',
      });

      linesRemoved++;
      oldIdx++;
    } else {
      // Addition only
      if (currentHunkType === 'unchanged') {
        flushHunk();
        currentHunkType = 'addition';
      }

      const addLine: DiffLine = {
        old_lineno: null,
        new_lineno: newIdx + 1,
        type: '+',
        content: nLine,
      };

      currentHunkLines.push(addLine);
      unifiedLines.push(addLine);
      splitLines.push({
        leftLineNumber: undefined,
        leftContent: undefined,
        leftType: 'empty',
        rightLineNumber: newIdx + 1,
        rightContent: nLine,
        rightType: 'added',
      });

      linesAdded++;
      newIdx++;
    }
  }

  flushHunk();

  const totalLines = Math.max(oldLines.length, newLines.length, 1);
  const similarityRatio = Math.max(0, 1 - (linesAdded + linesRemoved) / (totalLines * 2));

  const stats: DiffStats = {
    linesAdded,
    linesRemoved,
    linesModified,
    wordsAdded: linesAdded * 6,
    wordsRemoved: linesRemoved * 6,
    similarityRatio: Number(similarityRatio.toFixed(2)),
    totalOldLines: oldLines.length,
    totalNewLines: newLines.length,
  };

  return {
    baseRevisionId: baseRevId,
    targetRevisionId: targetRevId,
    companyId: 'company',
    policyType: 'terms_of_service',
    stats,
    splitLines,
    unifiedLines,
    hunks,
  };
}
